<template>
  <div class="dashboard-recruitment" :class="[hasStarted ? 'mission-started' : '', 'color-' + color].join(' ')">
    <header>
      <h3><slot name="header" /></h3>
      <i v-if="!hideExpiration">{{ expirationTimeDisplay }}</i>
      <a v-if="hideExpiration && showDetailsLink" @click.stop="openRecap">
        {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@OPEN_DETAILS") }}
      </a>
    </header>
    <article>
      <div v-if="overlayActivated" class="dashboard-recruitment-overlay">
        <slot name="overlay" />
      </div>
      <header>
        <div>
          <div class="row items-center justify-between">
            <h4>{{ recruitment.getQualification().getLabel() }}</h4>
            <span v-if="recruitment.isAE() && recruitment.isBoostedOffer()">
              <QIcon name="fa-sharp fas fa-bolt" />
              {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@BOOSTED_OFFER") }}
            </span>
          </div>
          <slot name="subtitle" />
        </div>
      </header>
      <!-- Display candidates and placeholders -->
      <section v-if="!shouldDisplayCandidateDetails && !recruitment.isBookingExpress()">
        <CandidateSmallCard
          v-for="(candidate, index) in candidates"
          :key="`no_placeholders_` + index"
          :candidate="candidate"
          :selected-icon="recruitment.isPermanent() ? 'icon-f-phone-forwarded' : 'icon-f-check'"
        />
        <template v-if="shouldDisplayPlaceholders">
          <CandidateSmallCard
            v-for="(candidate, index) in candidatePlaceholderDisplay"
            :key="`placeholders_` + index"
            :candidate="candidate"
            :class="loadingAnimation ? 'q-skeleton--anim-blink' : ''"
            :placeholder="true"
          />
        </template>
      </section>
      <!-- Display lone candidate details -->
      <section v-if="shouldDisplayCandidateDetails">
        <CandidateSmallCard :candidate="candidates[0]" :is-alone="true" :is-booking-express="recruitment.isBookingExpress()" />
        <!-- Display Booking Express candidate detail -->
      </section>
      <section v-if="!shouldDisplayCandidateDetails && recruitment.isBookingExpress()">
        <CandidateSmallCard :candidate="candidates[0]" :is-alone="true" :is-booking-express="recruitment.isBookingExpress()" />
      </section>
      <footer>
        <slot name="footer" />
      </footer>
    </article>
  </div>
</template>

<script>
  import { DatesHelpers } from "@core/helpers";
  import { CandidateDashboardModel, ICandidateDashboard, RecruitmentDashboardModel } from "@core/models";
  import CandidateSmallCard from "@etab/components/candidates/Candidate.small-card.vue";
  import { EXTRA_DISPLAY_LIMIT } from "@core/constants";
  import { ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL } from "@etab/stores/types";
  import { useDashboardStore } from "@etab/stores/dashboard";

  // eslint-disable-next-line no-undef
  const placeholderExtraOne = require("@etab/assets/img/placeholders/dashboard_extra_1.png");
  // eslint-disable-next-line no-undef
  const placeholderExtraTwo = require("@etab/assets/img/placeholders/dashboard_extra_2.png");
  // eslint-disable-next-line no-undef
  const placeholderExtraThree = require("@etab/assets/img/placeholders/dashboard_extra_3.png");
  // eslint-disable-next-line no-undef
  const placeholderExtraFour = require("@etab/assets/img/placeholders/dashboard_extra_4.png");

  const candidatesPlaceholder = [
    new CandidateDashboardModel({ [ICandidateDashboard.picture]: placeholderExtraOne }),
    new CandidateDashboardModel({ [ICandidateDashboard.picture]: placeholderExtraTwo }),
    new CandidateDashboardModel({ [ICandidateDashboard.picture]: placeholderExtraThree }),
    new CandidateDashboardModel({ [ICandidateDashboard.picture]: placeholderExtraFour }),
  ];

  // we show expiration clock for more than one day
  const EXPIRATION_TIME_REAL_TIME_THRESHOLD = 3 * 24 * 3600;

  export default {
    components: {
      CandidateSmallCard,
    },
    props: {
      recruitment: {
        type: Object,
        default: () => new RecruitmentDashboardModel(),
      },
      candidates: {
        type: Array,
        default: () => [],
      },
      overlayActivated: {
        type: Boolean,
        default: false,
      },
      hideExpiration: {
        type: Boolean,
        default: false,
      },
      color: {
        type: String,
        default: "primary",
      },
      showDetailsLink: {
        type: Boolean,
        default: false,
      },
      forcePlaceholder: {
        type: Boolean,
        default: false,
      },
      hasStarted: {
        type: Boolean,
        default: false,
      },
      loadingAnimation: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        expirationTimeClock: undefined,
        expirationTimeDisplay: "-",
      };
    },
    computed: {
      shouldDisplayCandidateDetails() {
        return this.candidates.length === 1 && (this.recruitment.isFilled() || this.recruitment.hasStarted());
      },
      candidatePlaceholderDisplay() {
        const amount = EXTRA_DISPLAY_LIMIT - this.candidates.length;
        return [...candidatesPlaceholder].splice(0, amount);
      },
      shouldDisplayPlaceholders() {
        // we display placeholders candidates as long as the recruitment is not expired unless there's 0 candidates
        return (!this.recruitment.isFilled() && !this.recruitment.isExpired() && !this.recruitment.hasStarted()) || this.candidates.length === 0;
      },
    },
    watch: {
      recruitment() {
        // if recruitment changes, we set the expiration time again
        this.initExpirationTime();
      },
    },
    created() {
      this.EXTRA_DISPLAY_LIMIT = EXTRA_DISPLAY_LIMIT;
      this.initExpirationTime();
    },
    beforeUnmount() {
      clearInterval(this.expirationTimeClock);
    },
    methods: {
      initExpirationTime() {
        const secondsBeforeExpiration = this.recruitment.getSecondsToExpiration();
        if (secondsBeforeExpiration === 0) {
          this.expirationTimeDisplay = this.$t("ETAB.PAGES.RECRUITMENT.@EXPIRED_DEMAND");
        } else if (secondsBeforeExpiration > EXPIRATION_TIME_REAL_TIME_THRESHOLD) {
          const days = Math.floor(secondsBeforeExpiration / (24 * 3600));
          this.expirationTimeDisplay = this.$tc("ETAB.PAGES.RECRUITMENT.@EXPIRATION_DAYS", days);
        } else {
          this.initExpirationClock();
        }
      },
      initExpirationClock() {
        let secondsBeforeExpiration = this.recruitment.getSecondsToExpiration();
        // reset the timer
        clearInterval(this.expirationTimeClock);

        this.expirationTimeClock = setInterval(() => {
          secondsBeforeExpiration -= 1;
          if (secondsBeforeExpiration > 0) {
            this.expirationTimeDisplay = this.$t("ETAB.PAGES.RECRUITMENT.@EXPIRATION_TIME", {
              time: DatesHelpers.convertSecondsToHHMMSS(secondsBeforeExpiration),
            });
          } else {
            this.expirationTimeDisplay = this.$t("ETAB.PAGES.RECRUITMENT.@EXPIRED_DEMAND");
            // demand expired, no need of timer anymore
            clearInterval(this.expirationTimeClock);
          }
        }, 1000);
      },
      openRecap() {
        const dashboardStore = useDashboardStore();
        dashboardStore[ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL]({ recap: new RecruitmentDashboardModel(this.recruitment) });
      },
    },
  };
</script>

<style lang="scss">
  .dashboard-recruitment {
    cursor: pointer;
    margin-bottom: 25px;
    width: 95vw;
    max-width: 500px;

    > header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: currentColor;
      padding: 0 15px;

      h3 {
        margin: 0;
        font-weight: bold;
        line-height: 35px;
      }

      i {
        color: $lightgrey;
        font-size: 12px;
        margin: 5px 0;
      }

      a {
        font-family: $common-font-family;
        font-size: 12px;
        color: $primary;
        text-decoration: underline;
      }
    }

    .dashboard-recruitment-address {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    > article {
      position: relative;
      border-top: 9px solid currentColor;
      border-radius: 4px;
      background: #ffffff;
      box-shadow: $card-shadow;
      overflow: hidden;
      min-height: 225px;
      .dashboard-recruitment-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba($primary, 0.65);
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        box-shadow: $card-shadow;
        text-align: center;
        padding: 20px 15px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        backdrop-filter: blur(1px);

        h3 {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 15px 0 0;
          margin: 0;
          color: white;
          line-height: 1.2;
          flex: 1;
          font-size: 1.2em;
        }

        > div {
          margin-top: 15px;
          .q-btn {
            margin: 0 5px;
          }
        }
      }

      > header {
        padding: 5px 15px;
        margin-bottom: 10px;
        @media screen and (max-width: $breakpoint-sm) {
          margin-bottom: 5px;
        }

        h4 {
          color: $primary;
          margin: 0;
          line-height: 30px;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        p {
          color: currentColor;
          margin: 0;
          font-size: 14px;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
      }

      > section {
        padding: 5px 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      > footer {
        padding: 5px;
        min-height: 75px;
        display: flex;
        align-items: center;
        justify-content: center;

        p {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0;
          text-align: center;

          .q-icon {
            margin-right: 10px;
            font-weight: bold;
          }

          span {
            line-height: 1.2;
          }
        }
      }
    }

    &.mission-started {
      > header {
        padding-bottom: 5px;
      }

      > article {
        border-top: none;

        > header {
          background-color: currentColor;

          h4,
          p > span {
            color: white !important;
          }
        }
      }
    }

    &.color-primary {
      color: $primary;

      .dashboard-recruitment-overlay {
        background-color: rgba($primary, 0.65);

        .q-btn {
          background: $primary !important;
        }
      }
    }

    &.color-secondary {
      color: $secondary;

      .dashboard-recruitment-overlay {
        background-color: rgba($secondary, 0.65);

        .q-btn {
          background: $secondary !important;
        }
      }
    }

    &.color-grey {
      color: $grey;

      .dashboard-recruitment-overlay {
        background-color: rgba($grey, 0.65);

        .q-btn {
          background: $grey !important;
        }
      }
    }
  }
</style>
